/* eslint-disable import/prefer-default-export */
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AccountPersistInfo, ApiInfo, Subscription } from 'Internal.CommonAPI';
import {
  getHighestActivePaidSubscription,
  getCustomerDisplayUser,
  getIsShorePayUser,
  getIsTrialUser,
  getActiveSubscriptions,
  getActiveSubscriptionNames,
  getGiftcardsV2User,
  getIsBetaUser,
  getIsInternalBetaUser,
  getIsTipsUser,
} from 'Account/selectors';
import { useLocalStorageState } from 'ahooks';
import { useIntl } from 'react-intl';
import config from 'Datev/config';
import { useCommonApi } from 'utils/apiClient/services/common';
import { handleErrorNotification } from '../utils';

export const useCommonInfo = (userToken?: string) => {
  const queryClient = useQueryClient();
  const intl = useIntl();
  // eslint-disable-next-line no-unused-vars ,  @typescript-eslint/no-unused-vars
  const [account, setAccount] =
    useLocalStorageState<AccountPersistInfo>('account');
  const Common = useCommonApi(userToken || undefined);
  return useQuery({
    queryKey: ['common', 'api'],
    queryFn: async () => {
      try {
        const response: ApiInfo = await Common.getInfo(userToken);

        queryClient.setQueryData(
          ['account', 'isActive'],
          response.permissions.includes('subscriptions.can_use_inventorum')
        );
        queryClient.setQueryData(
          ['account', 'activeSubscriptions'],
          response.account.subscriptions.filter((subs) => subs.active)
        );

        queryClient.setQueryData(
          ['account', 'permissions'],
          response.account.permissions
        );
        queryClient.setQueryData(
          ['account', 'highestActiveSubscription'],
          getHighestActivePaidSubscription(response.account.subscriptions)
        );

        queryClient.setQueryData(
          ['account', 'isPlusUser'],
          getCustomerDisplayUser(response.account.permissions)
        );
        queryClient.setQueryData(
          ['account', 'country'],
          response.account.country
        );

        queryClient.setQueryData(
          ['account', 'source'],
          response.account.source
        );
        queryClient.setQueryData(
          ['account', 'first_owner_id'],
          response.account.first_owner_id
        );
        queryClient.setQueryData(
          ['account', 'isTrialUser'],
          getIsTrialUser(response?.account.subscriptions)
        );

        queryClient.setQueryData(['account', 'email'], response.account.email);

        queryClient.setQueryData(['account', 'id'], response.account.id);
        queryClient.setQueryData(
          ['account', 'signUpDate'],
          response.account.account_creation
        );
        queryClient.setQueryData(
          ['account', 'country'],
          response.account.country
        );

        queryClient.setQueryData(
          ['account', 'merchant_id'],
          response.account.external_id
        );
        queryClient.setQueryData(
          ['account', 'activeSubscriptions'],
          getActiveSubscriptions(response?.account.subscriptions)
        );

        const data = {
          isPlusUser: getCustomerDisplayUser(response.account.permissions),
          highestActiveSubscription: getHighestActivePaidSubscription(
            response.account.subscriptions
          ),
          isShorePayUser: getIsShorePayUser(response.account.permissions),
          isTrialUser: getIsTrialUser(response?.account.subscriptions),
          isBetaUser: getIsBetaUser(response.account.permissions),
          isTipsUser: getIsTipsUser(response.account.permissions),
          activeSubscriptions: getActiveSubscriptions(
            response?.account.subscriptions
          ),
          isInternalBetaUser: getIsInternalBetaUser(
            response?.account.subscriptions
          ),
          country: response?.account.country,
          source: response?.account.source,
          isActive: response.account.permissions.includes(
            'subscriptions.can_use_inventorum'
          ),
          id: response.account.id,
          hasDatevSub: getActiveSubscriptionNames(
            response.account.subscriptions
          ).includes(config.datevSubscription),
          hasGiftcardsV2Sub: getGiftcardsV2User(response.account.permissions),
          email: response.account.email,
          signUpDate: response.account.account_creation,
          currency: response.account.currency,
          firstOwnerId: response.account.first_owner_id,
          merchant_id: response.account.external_id,
          userSuccessful: true,
          accountSuccessful: true,
          tokenSetAt: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
          ...(userToken !== undefined && { userToken }),
        };

        setAccount((prev) => ({ ...prev, ...data } as AccountPersistInfo));
        const activeSubscriptions: Subscription[] | undefined =
          queryClient.getQueryData(['account', 'activeSubscriptions']);
        if (activeSubscriptions !== undefined) {
          queryClient.setQueryData(
            ['account', 'activeSubscriptionNames'],
            activeSubscriptions.map((as) => as.subscription_plan.name)
          );

          const activeSubscriptionNames: string[] | undefined =
            queryClient.getQueryData(['account', 'activeSubscriptionNames']);
          if (activeSubscriptionNames !== undefined) {
            queryClient.setQueryData(
              ['account', 'isInternalBetaUser'],
              activeSubscriptionNames.includes('Internal Beta Access')
            );
          }
        }
        return response;
      } catch (error) {
        handleErrorNotification({
          error: (error as Error).message,
          lang: intl.locale.toLowerCase(),
        });
        return null;
      }
    },
    staleTime: 1500,
    enabled: false,
  });
};
